import { useMemo } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { segmentosResaltados } from 'src/services/ICD/icd-normalize';

import Iconify from 'src/components/iconify';

import IcdEntityDetail from './icd-entity-detail';

// ----------------------------------------------------------------------

// Ancho fijo para que los códigos de filas consecutivas se escaneen en vertical.
const ANCHO_CODIGO = 76;

const FUENTE_MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const escaparRegex = (texto) => texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Recientes y búsqueda por código no traen marcado <em> de la API: se resalta
// aquí lo tecleado para que esas filas no se lean distintas al resto.
function resaltarConsulta(texto, consulta) {
  if (!texto) return [];

  const palabras = String(consulta || '')
    .replace(/%/g, ' ')
    .split(/\s+/)
    .filter((palabra) => palabra.length >= 2)
    .map(escaparRegex);

  if (!palabras.length) return [{ texto, resaltado: false }];

  const alternativas = palabras.join('|');
  const patronCorte = new RegExp(`(${alternativas})`, 'gi');
  const patronPrueba = new RegExp(`^(?:${alternativas})$`, 'i');

  return texto
    .split(patronCorte)
    .filter(Boolean)
    .map((parte) => ({ texto: parte, resaltado: patronPrueba.test(parte) }));
}

// ----------------------------------------------------------------------

export default function IcdOptionRow({
  diagnostico,
  consulta = '',
  expandida = false,
  onAlternarDetalle,
  onAbrirCatalogo,
  onUsar,
}) {
  const {
    id,
    codigo,
    titulo,
    tituloHtml,
    capitulo,
    sinonimoCoincidente,
    esResidualNoEspecificado,
    esResidualOtro,
  } = diagnostico || {};

  const segmentos = useMemo(() => {
    const desdeApi = segmentosResaltados(tituloHtml);
    if (desdeApi.some((segmento) => segmento.resaltado)) return desdeApi;

    const plano = titulo || desdeApi.map((segmento) => segmento.texto).join('');
    return resaltarConsulta(plano, consulta);
  }, [tituloHtml, titulo, consulta]);

  // Sin esto, pulsar un control de la fila seleccionaría la opción del Autocomplete.
  const aislarPuntero = (evento) => {
    evento.preventDefault();
    evento.stopPropagation();
  };

  const manejar = (accion) => (evento) => {
    evento.stopPropagation();
    accion?.(diagnostico);
  };

  return (
    <Box sx={{ width: 1, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        sx={(theme) => ({
          px: 1,
          py: 0.75,
          borderRadius: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
          ...(expandida && { bgcolor: alpha(theme.palette.primary.main, 0.08) }),
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
        })}
      >
        <Chip
          size="small"
          variant="soft"
          color={codigo ? 'primary' : 'default'}
          label={codigo || '—'}
          sx={{
            mt: 0.25,
            width: ANCHO_CODIGO,
            flexShrink: 0,
            fontFamily: FUENTE_MONO,
            fontSize: '0.75rem',
            fontVariantNumeric: 'tabular-nums',
            '& .MuiChip-label': { px: 0.75, width: 1, textAlign: 'center' },
          }}
        />

        <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" component="div" sx={{ color: 'text.primary' }}>
            {segmentos.map((segmento, indice) => (
              <Box
                key={indice}
                component="span"
                sx={{
                  ...(segmento.resaltado && { fontWeight: 700, color: 'primary.main' }),
                }}
              >
                {segmento.texto}
              </Box>
            ))}
          </Typography>

          {!!sinonimoCoincidente && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              {`↳ coincide con «${sinonimoCoincidente}»`}
            </Typography>
          )}

          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            {!!capitulo && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {`Cap. ${capitulo}`}
              </Typography>
            )}

            {esResidualNoEspecificado && (
              <Chip size="small" variant="soft" color="warning" label="Sin especificar" />
            )}

            {esResidualOtro && (
              <Chip size="small" variant="soft" color="warning" label="Otro especificado" />
            )}
          </Stack>
        </Stack>

        <Tooltip title="Ver detalles" arrow placement="left">
          <IconButton
            size="small"
            color={expandida ? 'primary' : 'default'}
            onMouseDown={aislarPuntero}
            onClick={manejar(onAlternarDetalle)}
            aria-label="Ver detalles"
            aria-expanded={expandida}
            sx={{ flexShrink: 0, mt: 0.25 }}
          >
            <Iconify
              width={18}
              icon={expandida ? 'eva:arrow-ios-upward-fill' : 'eva:info-outline'}
            />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={expandida} unmountOnExit>
        <Box
          onMouseDown={aislarPuntero}
          onClick={(evento) => evento.stopPropagation()}
          sx={(theme) => ({
            mt: 0.5,
            p: 1.5,
            ml: `${ANCHO_CODIGO + 18}px`,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.grey[500], 0.08),
            border: `dashed 1px ${alpha(theme.palette.grey[500], 0.24)}`,
          })}
        >
          <IcdEntityDetail id={id} compacto />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Button size="small" variant="contained" onClick={manejar(onUsar)}>
              Usar este diagnóstico
            </Button>

            {/* Dentro del propio catálogo no hay a dónde abrirlo: sin manejador
                el botón existía y no hacía nada. */}
            {!!onAbrirCatalogo && (
              <Button
                size="small"
                color="inherit"
                onClick={manejar(onAbrirCatalogo)}
                endIcon={<Iconify width={16} icon="eva:expand-fill" />}
              >
                Abrir catálogo completo
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

IcdOptionRow.propTypes = {
  onUsar: PropTypes.func,
  consulta: PropTypes.string,
  expandida: PropTypes.bool,
  onAbrirCatalogo: PropTypes.func,
  onAlternarDetalle: PropTypes.func,
  diagnostico: PropTypes.shape({
    id: PropTypes.string,
    codigo: PropTypes.string,
    titulo: PropTypes.string,
    capitulo: PropTypes.string,
    tituloHtml: PropTypes.string,
    esResidualOtro: PropTypes.bool,
    sinonimoCoincidente: PropTypes.string,
    esResidualNoEspecificado: PropTypes.bool,
  }),
};
